import { useState } from "react";
import Box from "@mui/material/Box";
import { Icon } from "@iconify/react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { format } from "date-fns";
import { HStack, VStack, Error, useGetCompanyName } from "../components/utils";
import { Input, Label } from "../components/shared/InputField";
import {
  exportAOCReport,
  exportCaptableSummaryReport,
  exportMGT7Report,
  getAllotmentWindowReport,
  getAllTransactionBasedReport,
} from "../api/Report";
import { downloadExcel } from "../utils/DownloadFile";
import {
  ButtonSize,
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../pages/quickRound/CTAButtonComponents";
import { PopupHeader } from "../components/Headings";
import { Select } from "../components/shared/Select";

type ExportSheetDetailsProps = {
  onPrimaryAction: () => void;
  onSecondaryAction: () => void;
  reportName: string;
  companyId?: string;
};

type Option = {
  label: string;
  value: string;
};

type ExportFinancialYearSheetDetailsSubmit = {
  financialYear: string;
};

const ExportFinancialYearSheetDetails = ({
  onPrimaryAction = () => {},
  onSecondaryAction = () => {},
  reportName,
  companyId,
}: ExportSheetDetailsProps) => {
  const companyName = useGetCompanyName();
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (values: ExportFinancialYearSheetDetailsSubmit) => {
    setLoading(true);
    if (reportName === "MGT7 Report") {
      await exportMGT7Report(values.financialYear).then((res) => {
        const fileName = `MGT7 Report_${companyName}`;
        downloadExcel(res, fileName);
      });
    } else if (reportName === "AOC4 Report") {
      await exportAOCReport(values.financialYear).then((res) => {
        const fileName = `AOC4 Report_${companyName}`;
        downloadExcel(res, fileName);
      });
    }
    setLoading(false);
    onPrimaryAction();
  };

  const initialValues = {
    financialYear: "",
  };

  const excelSheetDetailsValidation = Yup.object({
    financialYear: Yup.string().required().label("Financial year"),
  });

  function generateFinancialYears(count: number = 10): Option[] {
    const currentYear = new Date().getFullYear();
    const options: Option[] = [];

    for (let i = 0; i < count; i++) {
      const startYear = currentYear - i;
      const endYear = startYear + 1;
      const label = `${startYear}-${String(endYear).slice(-2)}`; // "2023-24"
      options.push({ label, value: label });
    }

    return options;
  }

  return (
    <div>
      <PopupHeader
        text={reportName}
        subText={"Please select the financial year for which the report has to be generated"}
        onClick={() => onSecondaryAction()}
      />
      <VStack className="w-full px-8 py-7 gap-9">
        <Formik
          initialValues={initialValues}
          key="exportSheetDetails"
          onSubmit={(values) => {
            handleSubmit(values);
          }}
          validationSchema={excelSheetDetailsValidation}
        >
          {(formik) => (
            <div>
              <Form key="exportSheetDetails">
                <VStack className="w-full gap-4">
                  <VStack>
                    <Label>Financial Year</Label>
                    <Select
                      options={generateFinancialYears(25)}
                      valueGetter={(option) => option.value}
                      textGetter={(option) => option.label}
                      placeholder="Select Financial Year"
                      {...formik.getFieldProps("financialYear")}
                    />
                    {formik.touched.financialYear && formik.errors.financialYear && (
                      <Error text={formik.errors.financialYear} />
                    )}
                  </VStack>
                </VStack>
                <HStack className="justify-between mt-10 h-8">
                  <SecondaryCTAButton
                    event-name={`Cancel Download ${reportName}`}
                    buttonSize={ButtonSize.SMALL}
                    onClick={() => onSecondaryAction()}
                  >
                    Cancel
                  </SecondaryCTAButton>
                  <PrimaryCTAButton
                    event-name={`Download ${reportName}`}
                    buttonSize={ButtonSize.SMALL}
                    type="submit"
                  >
                    {!loading && <span>Submit</span>}

                    {loading && (
                      <HStack>
                        <p
                          className="indicator-progress"
                          style={{ display: "block" }}
                        >
                          Please wait...
                        </p>
                        <Icon
                          className="animate-spin"
                          icon="lucide:loader-2"
                          width={18}
                        />
                      </HStack>
                    )}
                  </PrimaryCTAButton>
                </HStack>
              </Form>
            </div>
          )}
        </Formik>
      </VStack>
    </div>
  );
};

export default ExportFinancialYearSheetDetails;
